import { IBreadcrumbItem, IBreadcrumbProps } from "@fluentui/react";
import { mergeStyles } from "@fluentui/react";
import { New16Regular } from "@fluentui/react-icons";
import { Divider, Paper } from "@mui/material";
import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { HEALTH_VIEW_DATA } from "../components/HealthView/Common/HealthView.constants";
import { HealthViewFiltersProps } from "../components/HealthView/Common/HealthView.interfaces";
import { HEALTH_VIEW_STYLES } from "../components/HealthView/Common/HealthView.styles";
import { HealthViewFilterPill } from "../components/HealthView/Common/HealthViewFilterPill";
import HealthViewTPID from "../components/HealthView/HealthViewTPID";
import { CatalystContainer } from "../components/HealthView/Containers/Catalyst/CatalystContainer";
import { BaselineSecurityHealthContainer } from "../components/HealthView/Containers/BaselineSecurityHealth/BaselineSecurityHealthContainer";
import HealthView from "../components/HealthView/HealthView";
import { logPageView } from "../helper/AppInsightsHelper";
import { CustomBreadcrumb } from "../SharedComponents/CustomBreadcrumb";

/* ── Navigation block: tabs row + optional toggle row, visually merged ── */
const navBlockClass = mergeStyles({
  backgroundColor: "#FFFFFF",
  borderBottom: "1px solid #EDEBE9",
});

const tabRowClass = mergeStyles({
  display: "flex",
  alignItems: "center",
  padding: "0 12px",
  borderBottom: "2px solid #EDEBE9",
});

const tabButtonBase = mergeStyles({
  border: "none",
  outline: "none",
  cursor: "pointer",
  backgroundColor: "transparent",
  padding: "10px 18px",
  fontSize: 14,
  fontWeight: 600,
  color: "#605E5C",
  borderBottom: "2px solid transparent",
  marginBottom: -2,
  transition: "color 0.15s, border-color 0.15s",
  selectors: {
    ":hover": {
      color: "#323130",
      backgroundColor: "#F3F2F1",
    },
  },
});

const tabButtonActive = mergeStyles({
  color: "#0078D4",
  borderBottomColor: "#0078D4",
  selectors: {
    ":hover": {
      color: "#0078D4",
      backgroundColor: "transparent",
    },
  },
});

const subTabRowClass = mergeStyles({
  display: "flex",
  alignItems: "center",
  gap: 4,
  padding: "6px 14px",
  backgroundColor: "#FAF9F8",
});

const subTabButton = mergeStyles({
  border: "1px solid #C8C6C4",
  outline: "none",
  cursor: "pointer",
  borderRadius: 4,
  padding: "4px 14px",
  fontSize: 13,
  fontWeight: 600,
  lineHeight: "20px",
  backgroundColor: "transparent",
  color: "#605E5C",
  transition: "background-color 0.15s, color 0.15s",
  selectors: {
    ":hover": {
      backgroundColor: "#E1DFDD",
    },
  },
});

const subTabButtonSelected = mergeStyles({
  backgroundColor: "#0078D4",
  color: "#FFFFFF",
  borderRadius: 4,
  borderColor: "#0078D4",
  selectors: {
    ":hover": {
      backgroundColor: "#106EBE",
    },
  },
});

export const HealthViewFiltersContext = createContext<HealthViewFiltersProps>(
  HEALTH_VIEW_DATA.HEALTH_VIEW.INITIAL_HEALTH_VIEW_FILTERS
);

export default function ExploreHealthPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "TenantHealthAssessment";
  const [selectedTab, setSelectedTab] = useState(initialTab);
  const initialSubTab = searchParams.get("subTab") || "ViewByTenant";
  const [selectedSubTab, setSelectedSubTab] = useState(initialSubTab);
  const [productFamilyValue, setProductFamily] = useState("");
  const [regionValue, setRegionValue] = useState("");
  const [segmentGroupValue, setSegmentGroupValue] = useState("");
  const [segmentValue, setSegmentValue] = useState("");
  const [subSegmentValue, setSubSegmentValue] = useState("");
  const [isS500Value, setIsS500] = useState("");
  const [industryValue, setIndustryValue] = useState("");
  const [initiatives, setInitiatives] = useState("");
  const [subInitiatives, setSubInitiatives] = useState("");
  const [agreementEndQuarter, setAgreementEndQuarter] = useState("");

  const handleTabChange = useCallback(
    (tabKey: string) => {
      setSelectedTab(tabKey);
      if (tabKey === "360View") {
        setSearchParams({ tab: tabKey, subTab: selectedSubTab }, { replace: true });
      } else {
        setSearchParams({ tab: tabKey }, { replace: true });
      }
    },
    [setSearchParams, selectedSubTab]
  );

  const handleSubTabChange = useCallback(
    (subTabKey: string) => {
      setSelectedSubTab(subTabKey);
      setSearchParams({ tab: "360View", subTab: subTabKey }, { replace: true });
    },
    [setSearchParams]
  );

  const breadcrumbProps: IBreadcrumbProps = {
    items: [
      { text: "Home", key: "explore-health-to-home", onClick: () => navigate("/") },
      {
        text: "Explore Health",
        key: "explore-health-with-product-name",
        isCurrentItem: true
      }
    ] as IBreadcrumbItem[],
    styles: HEALTH_VIEW_STYLES.HEALTH_PAGE.CSS_STYLES.HealthViewCustomBreadcrumb
  };

  const handleProviderValue = useMemo(
    () => ({
      productFamily: productFamilyValue,
      industry: industryValue,
      region: regionValue,
      segmentGroup: segmentGroupValue,
      segment: segmentValue,
      subSegment: subSegmentValue,
      isS500: isS500Value,
      initiatives: initiatives,
      subInitiatives: subInitiatives,
      agreementEndQuarter: agreementEndQuarter
    }),
    [
      productFamilyValue,
      industryValue,
      regionValue,
      segmentGroupValue,
      initiatives,
      segmentValue,
      subSegmentValue,
      isS500Value,
      agreementEndQuarter,
      subInitiatives
    ]
  );

  const filterProps = useMemo(
    () => ({
      productFamily: productFamilyValue,
      region: regionValue,
      segmentGroup: segmentGroupValue,
      segment: segmentValue,
      subSegment: subSegmentValue,
      isS500: isS500Value,
      industry: industryValue,
      agreementEndQuarter: agreementEndQuarter,
      initiatives: initiatives,
      subInitiatives: subInitiatives
    }),
    [
      productFamilyValue,
      regionValue,
      segmentGroupValue,
      segmentValue,
      subSegmentValue,
      isS500Value,
      industryValue,
      agreementEndQuarter,
      initiatives,
      subInitiatives
    ]
  );

  useEffect(() => {
    logPageView("Explore Health Page", window.location.href, filterProps);
  }, [filterProps]);

  return (
    <div className={HEALTH_VIEW_STYLES.HEALTH_PAGE.CLASS_NAMES.healthPageContainer}>
      <HealthViewFiltersContext.Provider value={handleProviderValue}>
        <Paper>
          <div style={{ display: "flex", width: "100%", justifyContent: "space-between" }}>
            <CustomBreadcrumb {...breadcrumbProps} />
            <div
              style={{
                marginTop: "10px",
                marginRight: "16px",
                marginBottom: "10px",
                backgroundColor: "#0078D4",
                color: "#FFFFFF",
                padding: "4px 12px",
                borderRadius: "9999px",
                display: "inline-flex",
                alignItems: "center",
                fontSize: "12px",
                fontWeight: 500
              }}
            >
              <New16Regular fontSize={12} style={{ marginRight: 4 }} />
              <span>Beta</span>
            </div>
          </div>
          <Divider />
        </Paper>

        {/* Navigation block */}
        <div className={navBlockClass}>
          <div className={tabRowClass}>
            <button
              className={`${tabButtonBase} ${selectedTab === "360View" ? tabButtonActive : ""}`}
              onClick={() => handleTabChange("360View")}
            >
              Business Health
            </button>
            <button
              className={`${tabButtonBase} ${selectedTab === "TenantHealthAssessment" ? tabButtonActive : ""}`}
              onClick={() => handleTabChange("TenantHealthAssessment")}
            >
              Ready for AI
            </button>
          </div>
          {selectedTab === "360View" && (
            <div className={subTabRowClass}>
              <button
                className={`${subTabButton} ${selectedSubTab === "ViewByTenant" ? subTabButtonSelected : ""}`}
                onClick={() => handleSubTabChange("ViewByTenant")}
              >
                View by Tenant
              </button>
              {/* View by TPID button hidden — page still reachable via ?subTab=ViewByTPID. Preserved for future re-enable.
              <button
                className={`${subTabButton} ${selectedSubTab === "ViewByTPID" ? subTabButtonSelected : ""}`}
                onClick={() => handleSubTabChange("ViewByTPID")}
              >
                View by TPID
              </button>
              */}
            </div>
          )}
        </div>

        {/* Tab content */}
        {selectedTab === "360View" && (
          <div style={{ padding: "0 8px" }}>
            {selectedSubTab === "ViewByTenant" && (
              <>
                <Paper style={{ marginTop: 10 }}>
                  <HealthViewFilterPill
                    onProductFamilyChange={setProductFamily}
                    onRegionChange={setRegionValue}
                    onIndustryChange={setIndustryValue}
                    onAgreementEndQuarterChange={setAgreementEndQuarter}
                    onSegmentGroupChange={setSegmentGroupValue}
                    onSegmentChange={setSegmentValue}
                    onSubSegmentChange={setSubSegmentValue}
                    onIsS500Change={setIsS500}
                    onInitiativesChange={setInitiatives}
                    onSubInitiativesChange={setSubInitiatives}
                  />
                </Paper>
                <HealthView />
              </>
            )}
            {selectedSubTab === "ViewByTPID" && (
              <>
                <Paper style={{ marginTop: 10 }}>
                  <HealthViewFilterPill
                    hideIntune
                    onProductFamilyChange={setProductFamily}
                    onRegionChange={setRegionValue}
                    onIndustryChange={setIndustryValue}
                    onAgreementEndQuarterChange={setAgreementEndQuarter}
                    onSegmentGroupChange={setSegmentGroupValue}
                    onSegmentChange={setSegmentValue}
                    onSubSegmentChange={setSubSegmentValue}
                    onIsS500Change={setIsS500}
                    onInitiativesChange={setInitiatives}
                    onSubInitiativesChange={setSubInitiatives}
                  />
                </Paper>
                <HealthViewTPID />
              </>
            )}
          </div>
        )}
        {selectedTab === "Catalyst" && <CatalystContainer />}
        {selectedTab === "TenantHealthAssessment" && <BaselineSecurityHealthContainer />}
      </HealthViewFiltersContext.Provider>
    </div>
  );
}
